import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { InfiniteList } from '../InfiniteScroll/InfiniteList'
import { Message } from '../Message'
import { MessageInput } from '../MessageInput'
import { TypingIndicator } from '../TypingIndicator'
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll'
import type { MessageListProps, ChatMessage } from '../../types'
import { useAppAuth } from '@/contexts/app-auth.context'

export const MessageList: React.FC<MessageListProps> = ({
  threadId,
  messages,
  loadEventsAroundMessage,
  hasMore,
  loading,
  className = '',
  typingUsers = [],
  scrollTo,
  companyId,
  hasMoreNewer = false,
  isDeeplinked = false,
  onLoadMore,
  onEmailClick,
  onSessionDetailsClick,
  isSuperAdmin,
  onSendMessage,
  loadMoreNewerMessages,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { user } = useAppAuth()
  const currentUserId = user?.id
  const prevThreadId = useRef(threadId)
  const scrollHeightBeforeLoad = useRef(0)
  const prevMessagesLength = useRef(messages.length)
  const isLoadingRef = useRef(false)
  const [animationComplete, setAnimationComplete] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // Only show loading when we have a threadId and are actually loading
  const effectiveLoading = threadId ? loading : false

  // Reset initial load tracking when thread changes
  useEffect(() => {
    if (prevThreadId.current !== threadId) {
      setHasInitiallyLoaded(false)
      prevThreadId.current = threadId
    }
  }, [threadId])

  // Track when fade animation completes and mark initial load as complete
  useEffect(() => {
    if (messages.length > 0) {
      setAnimationComplete(false)
      // Wait for 1.2s fade animation + buffer
      const timer = setTimeout(() => {
        setAnimationComplete(true)
        // Mark this thread as having completed its initial load
        if (!hasInitiallyLoaded) {
          setHasInitiallyLoaded(true)
        }
      }, 1300)
      return () => clearTimeout(timer)
    }
  }, [messages.length, threadId])

  // Use our infinite scroll hook - observe oldest message instead of sentinel
  const { oldestMessageRef, newestMessageRef } = useInfiniteScroll({
    onLoadMore: async () => {
      if (isLoadingRef.current) return

      isLoadingRef.current = true
      scrollHeightBeforeLoad.current = scrollRef.current?.scrollHeight || 0
      try {
        await onLoadMore()
      } finally {
        isLoadingRef.current = false
      }
    },
    hasMore,
    loading: effectiveLoading,
    enabled: hasInitiallyLoaded,
    scrollContainer: scrollRef.current,
    messages,
    animationComplete,
    // Newer messages support (for deeplinks)
    onLoadMoreNewer: loadMoreNewerMessages
      ? async () => {
          if (isLoadingRef.current) return
          isLoadingRef.current = true
          try {
            await loadMoreNewerMessages()
          } finally {
            isLoadingRef.current = false
          }
        }
      : undefined,
    hasMoreNewer,
    isDeeplinked,
  })

  // Restore scroll position after loading older messages
  const restoreScrollPosition = useCallback(() => {
    if (!scrollRef.current) return
    if (scrollTo === 'end') return // Don't restore when scrolling to end

    const currentLength = messages.length
    const previousLength = prevMessagesLength.current

    // If messages increased and we have a saved scroll height, we loaded older messages
    if (currentLength > previousLength && scrollHeightBeforeLoad.current > 0) {
      const container = scrollRef.current
      const heightDiff = container.scrollHeight - scrollHeightBeforeLoad.current

      // Maintain visual position by adjusting scrollTop
      container.scrollTop = heightDiff
      scrollHeightBeforeLoad.current = 0
    }

    // Update the previous length for next comparison
    prevMessagesLength.current = currentLength
  }, [messages.length, scrollTo])

  useLayoutEffect(() => {
    restoreScrollPosition()
  }, [restoreScrollPosition])

  // Track if we've already scrolled to end to prevent re-scrolling
  const hasScrolledToEnd = useRef(false)

  // Reset scroll tracking when thread changes or scrollTo changes
  useEffect(() => {
    hasScrolledToEnd.current = false
    // Reset direct load attempt when scrollTo changes (new message to find)
    hasTriedDirectLoad.current = false
    maxSearchAttempts.current = 0
    searchingForMessage.current = false
  }, [threadId, scrollTo])

  // Scroll to end of thread
  const scrollToEnd = useCallback(() => {
    if (!scrollRef.current || hasScrolledToEnd.current) return

    const container = scrollRef.current
    hasScrolledToEnd.current = true // Mark as scrolled

    // Scroll to the very bottom of the thread
    container.scrollTop = container.scrollHeight

    // Use requestAnimationFrame for smooth scroll and ensure we're at the bottom
    requestAnimationFrame(() => {
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })

    // Double-check after a short delay to handle any async content loading
    setTimeout(() => {
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }, 100)
  }, [])

  // Scroll to end of thread if scrollTo=end is specified - ONLY ONCE on initial load
  useEffect(() => {
    if (!scrollRef.current || !scrollTo || scrollTo !== 'end') return
    if (messages.length === 0 || loading) return
    if (hasScrolledToEnd.current) return // Only scroll once

    // Wait for messages to render and DOM to be ready
    const timer = setTimeout(scrollToEnd, 300)

    // Also try to scroll when container size changes (handles dynamic content)
    let resizeObserver: ResizeObserver | null = null
    if (scrollRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        if (!hasScrolledToEnd.current && scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
      resizeObserver.observe(scrollRef.current)
    }

    return () => {
      clearTimeout(timer)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [messages, scrollTo, loading, threadId, scrollToEnd])

  // Scroll to specific message - extracted logic
  const scrollToMessage = useCallback((messageId: string) => {
    if (!scrollRef.current) {
      return
    }

    const messageElement = scrollRef.current.querySelector(
      `[data-message-id="${messageId}"]`,
    ) as HTMLElement

    if (messageElement && scrollRef.current) {
      // Use native scrollIntoView which correctly handles flex-col-reverse,
      // Framer Motion transforms, and nested scroll containers
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Highlight the message with a more prominent animation
      messageElement.style.transition = 'background-color 0.5s ease-in-out'
      messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.25)' // More visible highlight

      // Pulse effect
      setTimeout(() => {
        messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'
      }, 300)

      setTimeout(() => {
        messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.25)'
      }, 600)

      setTimeout(() => {
        messageElement.style.backgroundColor = ''
      }, 2000)
    }
  }, [])

  const searchingForMessage = useRef(false)
  const maxSearchAttempts = useRef(0)
  const MAX_SEARCH_ATTEMPTS = 3
  const hasTriedDirectLoad = useRef(false)
  const hasScrolledToMessageId = useRef<string | null>(null)
  const scrollToThreadIdRef = useRef<string | null>(null)

  useEffect(() => {
    hasScrolledToMessageId.current = null
    searchingForMessage.current = false
    maxSearchAttempts.current = 0
    hasTriedDirectLoad.current = false
    scrollToThreadIdRef.current = null
  }, [threadId])

  useEffect(() => {
    if (!scrollRef.current || !scrollTo || scrollTo === 'end') return
    if (messages.length === 0 || loading) return
    if (hasScrolledToMessageId.current === scrollTo) return

    const targetMessage = messages.find(m => m.id === scrollTo)

    if (targetMessage) {
      searchingForMessage.current = false
      maxSearchAttempts.current = 0
      hasTriedDirectLoad.current = false
      hasScrolledToMessageId.current = scrollTo
      scrollToThreadIdRef.current = threadId

      // Delay to allow Framer Motion entry animations (opacity + y transform)
      // to settle before scrollIntoView measures element position
      const timer = setTimeout(() => {
        scrollToMessage(scrollTo)
      }, 500)

      return () => clearTimeout(timer)
    } else {
      if (
        scrollToThreadIdRef.current !== null &&
        scrollToThreadIdRef.current !== threadId
      ) {
        return
      }

      if (loadEventsAroundMessage && !hasTriedDirectLoad.current) {
        // Direct load: fetch messages centered around target message
        hasTriedDirectLoad.current = true
        searchingForMessage.current = true

        loadEventsAroundMessage(scrollTo)
          .then(() => {
            searchingForMessage.current = false
          })
          .catch(() => {
            searchingForMessage.current = false
          })
      } else if (
        hasMore &&
        !searchingForMessage.current &&
        maxSearchAttempts.current < MAX_SEARCH_ATTEMPTS
      ) {
        // Fallback: incrementally load older messages
        searchingForMessage.current = true
        maxSearchAttempts.current += 1

        onLoadMore().finally(() => {
          searchingForMessage.current = false
        })
      } else if (maxSearchAttempts.current >= MAX_SEARCH_ATTEMPTS) {
        maxSearchAttempts.current = 0
        searchingForMessage.current = false
        hasTriedDirectLoad.current = true
      }
    }
  }, [
    messages,
    scrollTo,
    loading,
    hasMore,
    threadId,
    scrollToMessage,
    onLoadMore,
    loadEventsAroundMessage,
  ])

  // Scroll to bottom for initial load - extracted logic
  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [])

  // Scroll to bottom only for initial load/thread switching, NOT infinite scroll
  useLayoutEffect(() => {
    if (!scrollRef.current || messages.length === 0) return
    if (scrollTo === 'end') return // Skip if scrolling to end
    if (scrollTo && scrollTo !== 'end') return // Skip if scrolling to specific message
    if (hasInitiallyLoaded) return // Only scroll on initial load

    // Scroll to bottom for initial fetch
    const timer = setTimeout(scrollToBottom, 50)

    return () => clearTimeout(timer)
  }, [messages.length, hasInitiallyLoaded, scrollTo, scrollToBottom])

  const renderMessage = useCallback(
    (message: ChatMessage, index: number) => (
      <div data-message-id={message.id}>
        <Message
          key={message.id}
          message={message}
          currentUserId={currentUserId}
          index={index}
          threadId={threadId}
          companyId={companyId}
          onEmailClick={onEmailClick}
          onSessionDetailsClick={onSessionDetailsClick}
          isSuperAdmin={isSuperAdmin}
        />
      </div>
    ),
    [
      currentUserId,
      threadId,
      companyId,
      onEmailClick,
      onSessionDetailsClick,
      isSuperAdmin,
    ],
  )

  // Show empty state if no threadId OR if we have threadId but no messages and not loading
  // (this handles the case where threadId is set but doesn't belong to current company)
  if (
    !threadId ||
    (threadId && messages.length === 0 && !loading && !effectiveLoading)
  ) {
    return (
      <div className="flex flex-1 overflow-y-auto overflow-x-hidden relative">
        <div className="flex flex-1 items-center justify-center">
          <div className="flex-1 text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-foreground mb-2">
              {!threadId ? 'Select a conversation' : 'No messages'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              {!threadId
                ? 'Choose a conversation from the sidebar to view messages'
                : 'This conversation has no messages yet'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const selectedThread =
    messages.length > 0
      ? {
          type: threadId.includes('email')
            ? 'email'
            : ('phone' as 'email' | 'phone'),
        }
      : null

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <AnimatePresence mode="wait">
        <motion.div
          key={threadId} // Key ensures re-animation when thread changes
          ref={scrollRef}
          className="flex-1 overflow-y-auto overflow-x-hidden relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.2,
          }}
        >
          <InfiniteList
            messages={messages}
            hasMore={hasMore}
            loading={effectiveLoading}
            renderMessage={renderMessage}
            oldestMessageRef={oldestMessageRef}
            newestMessageRef={newestMessageRef}
            isDeeplinked={isDeeplinked}
            hasMoreNewer={hasMoreNewer}
            loadingNewer={effectiveLoading && isDeeplinked}
          />
        </motion.div>
      </AnimatePresence>

      {/* Typing indicator */}
      <AnimatePresence>
        {typingUsers.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <TypingIndicator typingUsers={typingUsers} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message input */}
      {onSendMessage && selectedThread && (
        <MessageInput
          onSendMessage={onSendMessage}
          threadType={selectedThread.type}
          placeholder={`Type a ${selectedThread.type === 'email' ? 'message' : 'text message'}...`}
        />
      )}
    </div>
  )
}
